import { buildAdminSidebarItems } from '@/app/layouts/datasetAdminSidebarItems';
import type { SideBarItem } from '@/app/shell/SideBar';
import { ROUTES } from '@/shared/constants/routes';
import type { TFunction } from 'i18next';
import {
  Bell,
  BookOpen,
  CalendarClock,
  Clapperboard,
  Database,
  FileSpreadsheet,
  FileText,
  LayersPlus,
  LayoutDashboard,
  Presentation,
  UploadCloud,
} from 'lucide-react';

export const COLLAPSIBLE_SIDEBAR_GROUPS = ['operations', 'admin-config', 'advanced'] as const;

type SidebarItemOptions = {
  sourceCount: number | undefined;
  shareCount: number | undefined;
  showDiagnostic: boolean;
  showReports: boolean;
  showPresentations: boolean;
  showVideos: boolean;
  credits: 'internal' | 'customer' | null;
};

export function buildDatasetSidebarItems(
  t: TFunction,
  {
    sourceCount,
    shareCount,
    showDiagnostic,
    showReports,
    showPresentations,
    showVideos,
    credits,
  }: SidebarItemOptions,
): SideBarItem[] {
  const sideBarItems: SideBarItem[] = [
    {
      key: 'datasources',
      label: t('dataset.sidebar.datasources'),
      icon: Database,
      path: ROUTES.DATASET,
      group: 'data-modeling',
      count: sourceCount ? String(sourceCount) : undefined,
      requiredPermission: 'source:manage',
    },
    {
      key: 'datastudio',
      label: t('dataset.sidebar.datastudio'),
      icon: FileSpreadsheet,
      path: ROUTES.DATA_STUDIO,
      group: 'data-modeling',
    },
    {
      key: 'dashboards',
      label: t('dataset.sidebar.dashboards'),
      icon: LayoutDashboard,
      path: ROUTES.DASHBOARDS,
      group: 'data-modeling',
    },
    ...(showPresentations
      ? [
          {
            key: 'presentations',
            label: t('dataset.sidebar.presentations'),
            icon: Presentation,
            path: ROUTES.PRESENTATIONS,
            group: 'data-modeling',
          },
          {
            key: 'documents',
            label: t('dataset.sidebar.documents'),
            icon: FileText,
            path: ROUTES.DOCUMENTS,
            group: 'data-modeling',
          },
          ...(showVideos
            ? [
                {
                  key: 'videos',
                  label: t('dataset.sidebar.videos'),
                  icon: Clapperboard,
                  path: ROUTES.VIDEOS,
                  group: 'data-modeling',
                },
              ]
            : []),
        ]
      : []),
    {
      key: 'knowledge',
      label: t('dataset.sidebar.knowledge'),
      icon: BookOpen,
      path: ROUTES.KNOWLEDGE,
      group: 'data-modeling',
    },
    {
      key: 'scheduler',
      label: t('schedule.cronRefresh.title'),
      icon: CalendarClock,
      path: ROUTES.SCHEDULER,
      group: 'operations',
      requiredPermission: 'schedule:view',
    },
    {
      key: 'alerts',
      label: t('alerts.title'),
      icon: Bell,
      path: ROUTES.ALERTS,
      group: 'operations',
    },
    {
      key: 'reports',
      label: t('reports.title'),
      icon: FileText,
      path: ROUTES.REPORTS,
      group: 'operations',
      requiredPermission: 'workspace:view',
    },
    {
      key: 'data-shares',
      label: t('dataset.sidebar.dataShares'),
      icon: LayersPlus,
      path: ROUTES.DATA_SHARES,
      group: 'operations',
      requiredPermission: 'data_share:manage',
      count: shareCount ? String(shareCount) : undefined,
    },
    {
      key: 'egress',
      label: t('dataset.sidebar.dataEgress'),
      icon: UploadCloud,
      path: ROUTES.DATA_EGRESS,
      group: 'operations',
      requiredPermission: 'data_egress:manage',
    },
  ];

  sideBarItems.push(...buildAdminSidebarItems(t, { showDiagnostic, credits }));

  return showReports ? sideBarItems : sideBarItems.filter((item) => item.key !== 'reports');
}
